define('services/featureToggleService',[
    'lib/bluebird',
    'lib/knockout',
    'lib/lodash4',
    'services/apiCore',
    'services/authService',
    'services/loggingService'
], function (Promise, ko, _, apiCore, authService, logger) {
    'use strict';

    /**
     * A service abstraction around obtaining the feature flags for the current user session.
     */
    var FeatureToggleService = function () {
        var self = this;

        //Cache map of computeds created for each featureKey requested
        var featureToggleCache = {};

        function buildFeatureToggleObservable (featureKey) {
            var observable = ko.observable(false);
            var promise = new Promise(function (resolve, reject) {
                observable.resolve = resolve;
                observable.reject = reject;
            });
            observable.then = _.bind(promise.then, promise);
            observable.catch = _.bind(promise.catch, promise);
            observable.finally = _.bind(promise.finally, promise);

            featureToggleCache[featureKey] = observable;

            return observable;
        }

        function initializeFeatureToggleValue(observables, featureKeys) {
            var loadComputed = ko.computed(function () {
                if (authService.isLoggedIn()) {
                    apiCore.v2.get('/featureToggles', {feature: featureKeys})
                        .then(function (features) {
                            _.forEach(featureKeys, function (featureKey) {
                                var observable = observables[featureKey];
                                var toggleValue = !!features[featureKey];
                                observable(toggleValue);
                                observable.resolve(toggleValue);
                            });
                        })
                        .catch(function (error) {
                            logger.error('Unable to load feature toggle', error);
                            _.forEach(featureKeys, function (featureKey) {
                                var observable = observables[featureKey];
                                observable.reject(error);
                            });
                        })
                        .finally(function () {
                            loadComputed.dispose();
                        });
                }
            });
        }

        self.featureTogglesLoaded = ko.computed(function () {
            return authService.isLoggedIn();
        });

        /**
         * This returns an observable, NOT a boolean
         * @param featureKey
         * @returns observable
         */
        self.getFeatureToggle = function (featureKey) {
            if (_.has(featureToggleCache, featureKey)) {
                return featureToggleCache[featureKey];
            } else {
                var observable = buildFeatureToggleObservable(featureKey);
                var obsMap = {};
                obsMap[featureKey] = observable;
                initializeFeatureToggleValue(obsMap, [featureKey]);
                return observable;
            }
        };

        /**
         * Get an array of feature toggles which are enabled.
         * Passed in features which are not enabled will be omitted from the return array.
         *
         * @param {array} featureKeys feature toggles to check
         * @returns {array} an array of enabled feature toggles
         */
        self.getFeatureTogglesArray = function(featureKeys) {
            var enabledFeatures = [];
            _.forEach(featureKeys, function (key) {
                return self.getFeatureToggle(key).then(function (isEnabled) {
                    if (isEnabled) {
                        enabledFeatures.push(key);
                    }
                });
            });
            return enabledFeatures;
        };

        self.preloadFeatureToggles = function (featureKeys) {
            var pairs = _.map(featureKeys, function (feature) {
                var observable = buildFeatureToggleObservable(feature);
                return [ feature, observable ];
            });

            var observables = _.fromPairs(pairs);

            initializeFeatureToggleValue(observables, featureKeys);
        };
    };

    var service = new FeatureToggleService();

    service.preloadFeatureToggles([
        'adminBetterErrors',
        'adminBetterLoading',
        'adminGraphsUsePolling',
        'archGranularPermissions',
        'authorization.division.management',
        'CM-4564-deprecate-cm-audit',
        'CM-4564-use-audit-async-api',
        'PURE-5459_DocumentViewer_Decomposition',
        'PURE-6515_ContentManagement_DocumentAudits_Deprecation',
        'PURE-6363_new_resource_center_URI',
        'coaching.usability.add-to-existing',
        'dialer.automaticTimeZoneMapping',
        'dialer.liveVoiceAbandonCalc',
        'qm.release.keyword_spotting',
        'scheduleCoaching',
        'wfp',
        'wfpDecisionsDataDownloadDecomp',
        'digitalFirstPURE2942',
        'groups.enable-teams',
        'qm.release.evaluation.wem747',
        'qm.release.localized-numeric',
        'qm.release.cx1screenrecording',
        'qm.rec894',
        'qm.release.recording.activeScreenCount',
        'staCategories',
        'QM-18424_notice_of_deprecation_banner'
    ]);

    return service;
});

